import React, { useEffect, useMemo } from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { useMRTState } from '../../../../hooks/useMRTState';
import { getMRTDefaultProps } from '../../../../helpers/mrt/getMRTDefaultProps';
import { calcularResultado } from '../modules/useResultadoModule';
const labelGrupo = (g) => (g === 'diretoria' ? 'Diretoria' : 'Conselho Fiscal');
function VotosCell({ row, table }) {
    const { alterar, grupo, disabled, aclamacao } = table.options.meta.resultado;
    return (
        <TextField
            type="number"
            size="small"
            value={row.original.votos}
            disabled={disabled || aclamacao}
            inputProps={{ min: 0, step: 1, 'aria-label': `Votos da chapa ${row.original.numeroChapa}` }}
            onChange={(e) => alterar(grupo, 'votos', e.target.value, row.original.chapaId)}
        />
    );
}
function GrupoResultado({ grupo: g, modulo: m, parametro, isMobile, bloqueado }) {
    const mrt = useMRTState({ storageKey: `Eleicao.Resultado.${g.grupo}`, pageSizeDefault: Number(parametro?.paginacao) || 5 });
    const defaults = getMRTDefaultProps({ mrt, parametro, isMobile });
    const disabled = m.ocupado || bloqueado || !m.podeSalvar(g) || m.desatualizado || Boolean(m.erro);
    const aclamacao = g.modalidade === 'aclamacao',
        totais = calcularResultado(g);
    const columns = useMemo(
        () => [
            {
                id: 'resultado',
                header: 'Resultado da vaotação',
                columns: [
                    { accessorKey: 'numeroChapa', header: 'Número', size: 80 },
                    { accessorKey: 'nomeChapa', header: 'Chapa' },
                    { accessorKey: 'votos', header: 'Votos válidos', size: 150, enableSorting: false, Cell: VotosCell },
                ],
            },
        ],
        []
    );
    useEffect(() => {
        const ultima = Math.max(0, Math.ceil(g.itens.length / mrt.pagination.pageSize) - 1);
        if (mrt.pagination.pageIndex > ultima) mrt.setPagination((p) => ({ ...p, pageIndex: ultima }));
    }, [g.itens.length, mrt.pagination.pageSize, mrt.pagination.pageIndex, mrt.setPagination]);
    const situacao = g.resumoSalvo?.situacao;
    const lideres = g.itens
        .filter((i) => g.resumoSalvo?.lideres?.includes(i.chapaId))
        .map((i) => `Chapa ${i.numeroChapa} - ${i.nomeChapa}`)
        .join('; ');
    return (
        <Stack
            spacing={2}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: { xs: 1, sm: 2 }, minWidth: 0 }}
        >
            <Typography variant="h6">{labelGrupo(g.grupo)}</Typography>
            <Typography variant="body2">
                Votos possíveis: <strong>{g.totalPossiveis}</strong>{' '}
                {g.uuid ? '(total registrado na primeira gravação)' : '(unidades ativas da empresa)'}
            </Typography>
            {g.uuid && g.totalPossiveis !== g.totalUnidadesAtuais && (
                <Alert severity="info">
                    Atualmente existem {g.totalUnidadesAtuais} unidades ativas. A apuração preserva o total registrado de{' '}
                    {g.totalPossiveis}.
                </Alert>
            )}
            {g.chapasAlteradas && (
                <Alert severity="warning">
                    As chapas mudaram desde a última gravação. Revise todos os votos e salve novamente.
                    {!!g.chapasRemovidas.length &&
                        ` Fora da composição atual: ${g.chapasRemovidas.map((c) => `Chapa ${c.numeroChapa} - ${c.nomeChapa} (${c.votos == null ? 'aclamação' : c.votos + ' votos'})`).join('; ')}.`}
                </Alert>
            )}
            {!g.itens.length && <Alert severity="info">Nenhuma chapa inscrita neste grupo.</Alert>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                    select
                    label="Modalidade"
                    size="small"
                    value={g.modalidade}
                    disabled={disabled || !g.itens.length}
                    onChange={(e) => m.alterar(g.grupo, 'modalidade', e.target.value)}
                    sx={{ minWidth: 190 }}
                >
                    <MenuItem value="votacao">Votação</MenuItem>
                    <MenuItem value="aclamacao" disabled={g.itens.length !== 1}>
                        Aclamação
                    </MenuItem>
                </TextField>
                <TextField
                    label="Votos nulos"
                    size="small"
                    type="number"
                    value={g.votosNulos}
                    disabled={disabled || aclamacao || !g.itens.length}
                    onChange={(e) => m.alterar(g.grupo, 'votosNulos', e.target.value)}
                    inputProps={{ min: 0, step: 1 }}
                />
            </Stack>
            {aclamacao && (
                <Alert severity={g.itens.length === 1 ? 'info' : 'warning'}>
                    {g.itens.length === 1
                        ? 'Aclamação da chapa única. Não há contagem numérica de votos.'
                        : 'Aclamação não está disponível com esta composição. Selecione Votação.'}
                </Alert>
            )}
            <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                <MaterialReactTable
                    {...defaults}
                    meta={{ ...defaults.meta, resultado: { alterar: m.alterar, grupo: g.grupo, disabled, aclamacao } }}
                    columns={columns}
                    data={g.itens}
                    getRowId={(r) => String(r.chapaId)}
                    manualFiltering={false}
                    manualSorting={false}
                    manualPagination={false}
                    rowCount={undefined}
                    enableRowSelection={false}
                    enableRowActions={false}
                    enableColumnFilters={false}
                    enableGlobalFilter={false}
                    autoResetPageIndex={false}
                    state={{ ...defaults.state, columnFilters: [], globalFilter: '', showColumnFilters: false }}
                />
            </Box>
            {!aclamacao && (
                <Typography variant="body2">
                    Válidos: <strong>{totais.totalValidos}</strong> · Apurados (válidos + nulos):{' '}
                    <strong>{totais.totalApurado}</strong> · Não registrados: <strong>{totais.naoRegistrados}</strong>
                </Typography>
            )}
            {!aclamacao && !totais.completo && (
                <Alert severity="info">
                    Preencha todas as quantidades, inclusive zero. Os totais exibidos são parciais enquanto houver campos vazios.
                </Alert>
            )}
            {totais.excedeu && <Alert severity="error">O total apurado ultrapassa os votos possíveis.</Alert>}
            {g.resumoSalvo && !m.sujos[g.grupo] && (
                <Alert severity={situacao === 'empate' || situacao === 'revisar' ? 'warning' : 'info'}>
                    {situacao === 'aclamacao'
                        ? `Aclamação registrada: ${lideres}.`
                        : situacao === 'empate'
                          ? `Empate na maior votação: ${lideres}. Nenhuma chapa foi escolhida automaticamente.`
                          : situacao === 'apurado'
                            ? `Maior votação registrada: ${lideres}.`
                            : situacao === 'revisar'
                              ? 'Apuração salva precisa de revisão.'
                              : 'Apuração salva sem votos válidos para as chapas.'}
                </Alert>
            )}
            <Button
                type="button"
                variant="contained"
                color="success"
                sx={{ alignSelf: 'flex-start' }}
                disabled={
                    disabled ||
                    !g.itens.length ||
                    !totais.completo ||
                    totais.excedeu ||
                    Boolean(g.uuid && !m.sujos[g.grupo] && !g.chapasAlteradas)
                }
                onClick={() => m.salvar(g.grupo)}
            >
                Salvar apuração - {labelGrupo(g.grupo)}
            </Button>
        </Stack>
    );
}
export default function ResultadoTab({ modulo: m, eleicaoUuid, parametro, isMobile, bloqueado }) {
    if (!eleicaoUuid)
        return (
            <Alert severity="info" sx={{ my: 2 }}>
                Salve a eleição antes de registrar a apuração.
            </Alert>
        );
    return (
        <Stack spacing={2} sx={{ py: 2, minWidth: 0 }}>
            <Alert severity="info">
                Cada unidade ativa da empresa vale um voto em cada grupo. A apuração é salva nesta aba e ainda não finaliza a
                eleição.
            </Alert>
            <Button type="button" sx={{ alignSelf: 'flex-start' }} disabled={m.ocupado || bloqueado} onClick={m.recarregar}>
                Recarregar apuração
            </Button>
            {m.erro && <Alert severity="error">{m.erro}</Alert>}
            {m.desatualizado && (
                <Alert severity="warning">
                    Os dados de referência mudaram. Anote os votos digitados e recarregue a apuração antes de continuar.
                </Alert>
            )}
            {m.carregando && <Typography>Carregando apuração...</Typography>}
            {m.grupos.map((g) => (
                <GrupoResultado
                    key={g.grupo}
                    grupo={g}
                    modulo={m}
                    parametro={parametro}
                    isMobile={isMobile}
                    bloqueado={bloqueado}
                />
            ))}
        </Stack>
    );
}
